// libs
import React, { useMemo, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

// hooks
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

// style
import {
  TopSectionContainer,
  ImageSection,
  ProfileImage,
  HeaderContainer,
  HeaderText,
  ProfileDetailsSection,
  FirstSection,
  SecondSection,
  ThirdSection,
  RewardsHeading,
  MemberSince,
  PointsSection,
  Spent,
  ViewHistory,
  HistoryButton,
  RewardPointHeading,
  Points,
  Expiry,
  PointsDisplay,
} from './styles';

// components
import Header from 'shared/components/Header/MobileHeader/Header';
import Loader from 'shared/components/Loader';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// helpers
import { fetchRewardsPageData } from 'shared/store/RewardsPage/services';
import { AuthAPI } from 'shared/api/authResolver';
import {
  formatExpiry,
  formatMemberSinceDate,
} from 'shared/components/FootlockerRewards/components/RewardsPage/helpers';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import {
  TEXTS,
  RIGHT_ARROW_ICON_SIZE,
} from 'shared/components/FootlockerRewards/components/RewardsPage/constants';

// helpers
import {
  getImageFormat,
  getProfileData,
  getProfileImage,
} from 'shared/layouts/MyProfilePage/Mobile/helpers';

const TopSection = () => {
  const theme: Theme = useTheme();
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { viewHistoryUrl } = useRemoteConfig('rewardsPageData') || {};
  const ArrowRight = useStoreIcon(IconName.ARROW_RIGHT);
  const [imageData, setImageData] = useState<string>('');
  const [imageFormat, setImageFormat] = useState<string>('');

  const headingContent = useMemo(
    () => (
      <HeaderContainer>
        <HeaderText src="https://asset.footlocker.co.in/footlocker-images/FLX_rewards_logo.svg" />
      </HeaderContainer>
    ),
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch logged-in user data
        const custData = await AuthAPI.getLoggedInUser();
        const customerId = custData?.response?.customerData?.id;
        if (!customerId) {
          throw new Error('Customer ID not found. Please log in again.');
        }

        // Fetch rewards page data
        const data = await fetchRewardsPageData({
          headers: {},
          customerId: customerId, // Pass customerId in params
        });

        setApiData(data?.data?.userDetailsDTO);
        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        setError(err.message || 'Failed to load data. Please try again later.');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    getProfileData()
      .then((res) => {
        const user = res?.response?.user;
        // Try to derive a format hint from whatever we have — id from legacy
        // image_url, or the imageLink itself for Grahak.
        let formatHint = '';
        try {
          formatHint = user?.image_url ? new URL(user.image_url).searchParams.get('id') || '' : '';
        } catch {
          formatHint = '';
        }
        if (!formatHint) formatHint = user?.imageLink || '';
        setImageFormat(getImageFormat(formatHint));

        return getProfileImage(user).then((image) => {
          setImageData(image);
        });
      })
      .catch((e) => {
        console.error('Error fetching profile data:', e);
      });
  }, []);

  return (
    <>
      <Header
        isDiscountStripEnabled={false}
        showHamburger={false}
        HeaderContent={headingContent}
        showSearch={false}
        showBack
        showLogo={false}
        showBottomBorder
        showCart={false}
        showWishlist={false}
      />
      <TopSectionContainer>
        <ImageSection>
          <ProfileImage src={`data:${imageFormat};charset=utf-8;base64,${imageData}`} />
        </ImageSection>
      </TopSectionContainer>
      {!error && (
        <ProfileDetailsSection hide={isEmpty(apiData)}>
          {loading ? (
            <Loader />
          ) : (
            !isEmpty(apiData) && (
              <>
                <FirstSection>
                  <RewardsHeading>{TEXTS.FLX_REWARDS_HEADING}</RewardsHeading>
                  <MemberSince>
                    {TEXTS.MEMBER_SINCE}{' '}
                    {formatMemberSinceDate(apiData?.rewardDetails?.memberSinceDate)}
                  </MemberSince>
                </FirstSection>
                <SecondSection>
                  <PointsSection>
                    <RewardPointHeading>{TEXTS.REWARD_POINTS}</RewardPointHeading>
                    <PointsDisplay>
                      <Points>{apiData?.rewardDetails?.rewardPoints}</Points>
                      {!!apiData?.rewardDetails?.rewardPointsExpiryDate && (
                        <Expiry>
                          ({TEXTS.EXPIRY}
                          {formatExpiry(apiData?.rewardDetails?.rewardPointsExpiryDate)})
                        </Expiry>
                      )}
                    </PointsDisplay>
                  </PointsSection>
                  <Spent>
                    {TEXTS.SPENT}
                    {apiData?.rewardDetails?.spendAmount}
                  </Spent>
                </SecondSection>
                <ThirdSection onClick={() => window.open(viewHistoryUrl, '_self')}>
                  <ViewHistory>{TEXTS.VIEW_HISTORY}</ViewHistory>
                  <HistoryButton>
                    <ArrowRight color={theme.colors.primary} size={RIGHT_ARROW_ICON_SIZE} />
                  </HistoryButton>
                </ThirdSection>
              </>
            )
          )}
        </ProfileDetailsSection>
      )}
    </>
  );
};

export default TopSection;
